import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { Drawer, TextField, toast } from "@outpave/ds";
import ApiKeyRole, { API_KEY_DEFAULT_LIMITS } from "enums/ApiKeyRole";
import ApiVersion from "enums/ApiVersion";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import {
  ApiKey,
  ApiKeyPermissionCatalogue,
  UpdateApiKeyInput,
} from "hooks/useApiKeys";
import { toastMutationError } from "utils/toastMutationError";
import { Inline } from "components/layout/reusableLayouts";
import { canonicalizeIpOrCidr } from "utils/apiKeys/ipAllowlist";
import {
  getKeyRole,
  isSamePermissionSet,
  prunePermissions,
} from "utils/apiKeys/apiKeyPermissions";
import IpAllowlistField, { AllowListMode } from "./IpAllowlistField";
import ApiKeyAccessField from "./ApiKeyAccessField";

interface ApiKeyEditDrawerProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onUpdate: (id: string, input: UpdateApiKeyInput) => Promise<ApiKey>;
  // Null while the catalogue loads or after it fails: no access choice then.
  catalogue?: ApiKeyPermissionCatalogue | null;
}

/** Each of the two rate limit fields takes half the row and may shrink. */
const half = { flex: 1, minWidth: 0 } as const;

/**
 * A rate limit is a whole number of requests. GraphQL Int takes a negative one
 * happily and it becomes the limiter's max, so the form refuses it here.
 */
const isWholeCount = (value: string): boolean => {
  if (!value) return true;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0;
};

/**
 * A value that differs from the role default is an override. A value EQUAL to
 * it is ambiguous, because the form prefills a stored null with that same
 * number, so the STORED value breaks the tie: a key pinned to it stays pinned,
 * and one that was tracking the default keeps tracking.
 */
const resolveLimit = (
  value: string,
  stored: number | null,
  roleDefault: number,
): number | null => {
  if (!value) return null;
  const entered = Number(value);
  if (entered !== roleDefault) return entered;
  return stored === roleDefault ? stored : null;
};

const toDateInputValue = (epochMs?: number | null): string => {
  if (!epochMs) return "";
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

interface EditForm {
  name: string;
  role: ApiKeyRole;
  expiresAt: string;
  allowListMode: AllowListMode;
  allowListIps: string[];
  rateLimitSustained: string;
  rateLimitBurst: string;
}

const roleFromKey = (apiKey: ApiKey): ApiKeyRole =>
  (apiKey.role as ApiKeyRole) || ApiKeyRole.ReadOnly;

/**
 * The access decides the role, except that a key which still answers on API v1
 * keeps Admin: lowering it would take its v1 writes away.
 */
const getRole = (
  apiKey: ApiKey | null,
  permissions: string[],
  catalogue: ApiKeyPermissionCatalogue | null,
): ApiKeyRole => {
  const stored = apiKey ? roleFromKey(apiKey) : ApiKeyRole.ReadOnly;
  if (!catalogue) return stored;
  const keepsV1 =
    (apiKey?.minimumApiVersion ?? ApiVersion.V1) === ApiVersion.V1;
  if (keepsV1 && stored === ApiKeyRole.Admin) return ApiKeyRole.Admin;
  return getKeyRole(permissions, catalogue.permissions);
};

const getVersionHelperKey = (apiKey: ApiKey | null): string => {
  const v1Refused =
    (apiKey?.minimumApiVersion ?? ApiVersion.V1) > ApiVersion.V1;
  if (apiKey?.v2Enrolled) {
    return v1Refused ? "ApiKeyMinimumV2Enrolled" : "ApiKeyV2Enrolled";
  }
  return v1Refused ? "ApiKeyV2AccessCreateHelper" : "ApiKeyV2AccessHelper";
};

const formFromKey = (apiKey: ApiKey): EditForm => {
  const role = roleFromKey(apiKey);
  const stored = (apiKey.allowListIp || "").trim();
  const allowAll = !stored || stored.toUpperCase() === "ALL";
  const defaults = API_KEY_DEFAULT_LIMITS[role];
  return {
    name: apiKey.name || "",
    role,
    expiresAt: toDateInputValue(apiKey.expiresAt),
    allowListMode: allowAll ? "ALL" : "RESTRICT",
    allowListIps: allowAll
      ? []
      : stored
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((entry) => canonicalizeIpOrCidr(entry)),
    rateLimitSustained: String(
      apiKey.rateLimitSustained ?? defaults.sustainedPerHour,
    ),
    rateLimitBurst: String(apiKey.rateLimitBurst ?? defaults.burst),
  };
};

const ApiKeyEditDrawer: React.FC<ApiKeyEditDrawerProps> = ({
  open,
  apiKey,
  onClose,
  onUpdate,
  catalogue = null,
}) => {
  const { t } = useTranslation();
  // PlatformOwner, to match assertCanSetRateLimits in the ApiKey resolver.
  const canEditRateLimits = useCheckUserRole(Role.PlatformOwner);

  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [allowListMode, setAllowListMode] = useState<AllowListMode>("ALL");
  const [allowListIps, setAllowListIps] = useState<string[]>([]);
  const [allowListIpDraft, setAllowListIpDraft] = useState("");
  const [rateLimitSustained, setRateLimitSustained] = useState<string>("");
  const [rateLimitBurst, setRateLimitBurst] = useState<string>("");
  // What the key actually stored, which the prefilled fields can no longer say.
  const [storedLimits, setStoredLimits] = useState<{
    sustained: number | null;
    burst: number | null;
  }>({ sustained: null, burst: null });
  // Null until the access is edited: until then it follows the stored
  // permissions, whenever the catalogue arrives.
  const [editedPermissions, setEditedPermissions] = useState<string[] | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState<EditForm | null>(null);

  // Stored names the catalogue does not offer stay out of the selection, so
  // only a save that changes the selection drops them.
  const loadedPermissions = useMemo(
    () =>
      apiKey && catalogue
        ? prunePermissions(apiKey.permissions ?? [], catalogue.permissions)
        : [],
    [apiKey, catalogue],
  );
  const permissions = editedPermissions ?? loadedPermissions;
  const role = getRole(apiKey, permissions, catalogue);
  const roleDefaults = API_KEY_DEFAULT_LIMITS[role];
  const permissionsChanged = !isSamePermissionSet(
    permissions,
    loadedPermissions,
  );

  useEffect(() => {
    if (!apiKey) return;
    const form = formFromKey(apiKey);
    setLoaded(form);
    setName(form.name);
    setExpiresAt(form.expiresAt);
    setAllowListMode(form.allowListMode);
    setAllowListIps(form.allowListIps);
    setAllowListIpDraft("");
    setRateLimitSustained(form.rateLimitSustained);
    setRateLimitBurst(form.rateLimitBurst);
    setStoredLimits({
      sustained: apiKey.rateLimitSustained ?? null,
      burst: apiKey.rateLimitBurst ?? null,
    });
    setEditedPermissions(null);
    setSubmitting(false);
  }, [apiKey, open]);

  const hasChanges =
    !!loaded &&
    (name !== loaded.name ||
      role !== loaded.role ||
      expiresAt !== loaded.expiresAt ||
      allowListMode !== loaded.allowListMode ||
      allowListIps.join(",") !== loaded.allowListIps.join(",") ||
      allowListIpDraft.trim() !== "" ||
      rateLimitSustained !== loaded.rateLimitSustained ||
      rateLimitBurst !== loaded.rateLimitBurst ||
      permissionsChanged);

  // An access change that moves the key's role reconsiders its limits, as the
  // create drawer does. Done here rather than in an effect on `role`, which
  // would also fire on the prefill and wipe a stored override.
  const handleAccessChange = (next: string[]) => {
    setEditedPermissions(next);
    const nextRole = getRole(apiKey, next, catalogue);
    if (nextRole === role) return;
    const defaults = API_KEY_DEFAULT_LIMITS[nextRole];
    setRateLimitSustained(String(defaults.sustainedPerHour));
    setRateLimitBurst(String(defaults.burst));
    // Reconsidering the limits drops what the old role had pinned, so the new
    // role's defaults track until someone overrides them again.
    setStoredLimits({ sustained: null, burst: null });
  };

  const { requestLeave, dialog } = useUnsavedChangesGuard(hasChanges, onClose);

  // The X, backdrop, Escape and Cancel all leave here.
  const handleClose = () => {
    if (submitting) return;
    requestLeave();
  };

  const handleSubmit = async () => {
    if (!apiKey) return;
    if (!name.trim()) {
      toast.error(t("ApiKeyNameRequired"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIpDraft.trim()) {
      toast.error(t("ApiKeyConfirmIpEntry"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIps.length === 0) {
      toast.error(t("ApiKeyAddAtLeastOneIp"));
      return;
    }
    if (
      canEditRateLimits &&
      (!isWholeCount(rateLimitSustained) || !isWholeCount(rateLimitBurst))
    ) {
      toast.error(t("ApiKeyRateLimitWholeNumber"));
      return;
    }

    setSubmitting(true);
    try {
      const input: UpdateApiKeyInput = {
        name: name.trim(),
        role,
        expiresAt: expiresAt ? new Date(expiresAt).getTime() : null,
        allowListIp: allowListMode === "ALL" ? "ALL" : allowListIps.join(","),
        // Same rule as the create drawer: a value matching the role default is
        // sent as null, so the key keeps tracking future default changes. The
        // form prefills a stored null with that default, so sending the number
        // back would turn every save into a permanent override.
        ...(canEditRateLimits && {
          rateLimitSustained: resolveLimit(
            rateLimitSustained,
            storedLimits.sustained,
            roleDefaults.sustainedPerHour,
          ),
          rateLimitBurst: resolveLimit(
            rateLimitBurst,
            storedLimits.burst,
            roleDefaults.burst,
          ),
        }),
        // Enrolls a key that is not yet on API v2; otherwise sent only on a change, which ends its v2 tokens.
        ...(catalogue &&
          (!apiKey.v2Enrolled || permissionsChanged) && { permissions }),
      };
      await onUpdate(apiKey.id, input);
      toast.success(t("ApiKeyUpdatedToast"));
      onClose();
    } catch (err: unknown) {
      toastMutationError(err, t("ApiKeyUpdateError"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={t("Edit API key")}
        loading={submitting}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSubmit}
              disabled={submitting || !name.trim()}
            >
              {t("Save changes")}
            </Button>
          </>
        }
      >
        <TextField
          label={t("Name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          disabled={submitting}
        />

        {catalogue && (
          <ApiKeyAccessField
            catalogue={catalogue}
            selected={permissions}
            onSelectedChange={handleAccessChange}
            role={role}
            versionHelper={t(getVersionHelperKey(apiKey))}
            disabled={submitting}
          />
        )}

        <TextField
          label={t("Expires at (optional)")}
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          fullWidth
          disabled={submitting}
        />

        <IpAllowlistField
          mode={allowListMode}
          onModeChange={setAllowListMode}
          entries={allowListIps}
          onEntriesChange={setAllowListIps}
          draft={allowListIpDraft}
          onDraftChange={setAllowListIpDraft}
          disabled={submitting}
        />

        {canEditRateLimits && (
          <Inline gap="var(--space-5)" align="start" wrap={false}>
            <Box sx={half}>
              <TextField
                label={t("Sustained rate (per hour)")}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={rateLimitSustained}
                onChange={(e) => setRateLimitSustained(e.target.value)}
                fullWidth
                disabled={submitting}
              />
            </Box>
            <Box sx={half}>
              <TextField
                label={t("Burst rate (max in 10 seconds)")}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={rateLimitBurst}
                onChange={(e) => setRateLimitBurst(e.target.value)}
                fullWidth
                disabled={submitting}
              />
            </Box>
          </Inline>
        )}
      </Drawer>
      {dialog}
    </>
  );
};

export default ApiKeyEditDrawer;
